import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
    Box,
    Button,
    Typography,
    Modal,
    Stack,
    TextField,
    IconButton,
    Container,
    Paper,
    Checkbox,
    Link,
    Radio,
    RadioGroup,
    FormControlLabel,
    Divider,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
    dayjsUTC,
    months_abbreviations,
    MIN_DATE,
    MAX_DATE,
} from "../../helpers/dates";
import Icons from "../../helpers/icons";
import {
    CATEGORY,
    OWNER,
    describeRRule,
    updateStartEndRRule,
    requestCreateEvent,
    requestUpdateEvent,
} from "../../helpers/events";
import Colours from "../../helpers/colours";
import { get_currency } from "../../helpers/currency";
import { DEFAULT_HOTEL, GBH_ID, SanitizeHotelID } from "../../configuration";

import DropdownSearchSelector from "../DropdownSearchSelector";
import ModalConfirmation from "../ModalConfirmation";
import EventRepeatForm from "./EventRepeatForm";
import EventOccurrencesViewer from "./EventOccurrencesViewer";

const dayjs = dayjsUTC;

export const CustomDateButton = ({
    date,
    label,
    onChange = () => {},
    error = false,
    errorText = "",
    required = true,
    direction = "column",
    minDate = MIN_DATE,
    maxDate = MAX_DATE,
    disabled = false,
}) => {
    const parsedDate = !date
        ? null
        : typeof date.isValid === "function"
          ? date
          : dayjs(date);
    const isValidDate = parsedDate?.isValid?.() ?? false;

    const { year, month, day } = isValidDate
        ? {
              year: parsedDate.year(),
              month: parsedDate.month(),
              day: parsedDate.date(),
          }
        : { year: null, month: null, day: null };
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    // Forcing date picker to close when clicking outside the component (e.g., other page buttons)
    useEffect(() => {
        if (!open) return;

        const handleMouseDown = (e) => {
            if (containerRef.current?.contains(e.target)) return;
            if (e.target.closest("[data-popper-placement]")) return;
            setOpen(false);
        };

        document.addEventListener("mousedown", handleMouseDown, true);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown, true);
        };
    }, [open]);

    const ButtonField = (props) => {
        const { id, disabled, InputProps: { ref } = {} } = props;

        return (
            <Stack
                sx={{
                    justifyContent: "space-between",
                }}
                direction={direction}
                spacing={direction === "column" ? 0 : 1}
            >
                {label && (
                    <Typography
                        variant="subtitle2"
                        fontWeight={"bold"}
                        sx={{
                            minWidth: "20vh",
                            alignContent:
                                direction === "column" ? "baseline" : "center",
                        }}
                    >
                        {label}
                        {required && (
                            <span style={{ color: "red" }}>{" *"}</span>
                        )}
                    </Typography>
                )}
                <Button
                    ref={ref}
                    id={id}
                    disabled={disabled}
                    onClick={() => {
                        if (!disabled) setOpen((prev) => !prev);
                    }}
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{
                        width: "fit-content",
                        // maxWidth: "30vh",
                        height: "60px",
                        textTransform: "none",
                        color: "black",
                        justifyContent: "flex-end",
                        position: "relative",
                        borderColor: error ? "red" : "",
                    }}
                >
                    {isValidDate ? (
                        <Typography variant={"subtitle1"}>
                            {`${day} ${months_abbreviations[month]} ${year}`}
                        </Typography>
                    ) : (
                        <Typography variant={"subtitle1"} color="grey">
                            Select date
                        </Typography>
                    )}
                </Button>
                {errorText !== "" && (
                    <Typography variant="subtitle2" sx={{ color: "darkred" }}>
                        {errorText}
                    </Typography>
                )}
            </Stack>
        );
    };
    const ButtonDatePicker = (props) => {
        return (
            <DatePicker
                open={open}
                value={isValidDate ? parsedDate : null}
                onChange={(newValue) => {
                    if (newValue?.isValid?.()) {
                        onChange(newValue.startOf("day"));
                    }
                }}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                slots={{ field: ButtonField }}
                slotProps={{ field: { setOpen } }}
                minDate={minDate}
                maxDate={maxDate}
                disabled={disabled}
                views={["year", "month", "day"]}
                openTo="day"
                {...props}
            />
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div ref={containerRef}>
                <ButtonDatePicker
                    label={label ?? ""}
                    value={isValidDate ? parsedDate : null}
                    onChange={onChange}
                    disabled={disabled}
                />
            </div>
        </LocalizationProvider>
    );
};

export const ValidatedTextField = ({
    id,
    label,
    value,
    onChange,
    required = false,
    maxLength = 100,
    validate = () => true,
    errorMessage = "Invalid value",
    multiline = false,
    maxRows = undefined,
    minRows = undefined,
    errorForm,
}) => {
    const [touched, setTouched] = useState(false);

    const error = (touched || errorForm) && !validate(value);

    return (
        <Box id={id} flex={1}>
            {/* Label */}
            <Typography fontWeight="bold" variant="subtitle1">
                {label}
                {required && <span style={{ color: "red" }}> *</span>}
            </Typography>

            {/* TextField */}
            <TextField
                id={`${id}-textfield`}
                fullWidth
                multiline={multiline}
                maxRows={maxRows}
                minRows={minRows}
                autoComplete="off"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={() => setTouched(true)}
                error={error}
                helperText={
                    error
                        ? `${value.length}/${maxLength}   ${errorMessage}`
                        : `${value.length}/${maxLength}`
                }
                slotProps={{
                    htmlInput: { maxLength },
                }}
            />
        </Box>
    );
};

const EventCreateForm = ({
    eventDetails = {},
    copiedEvent = false,
    extraData = {},
    onClose = () => {},
    open,
    handleUpdate,
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const currencySymbol = get_currency(hotelID);
    const { auth } = useSelector((state) => state);
    const { user } = useSelector((state) => state.auth);
    const headers = useMemo(
        () => ({ Authorization: `Bearer ${auth.user.jwt}` }),
        [auth],
    );

    const editEvent =
        Object.keys(eventDetails).length > 0 && eventDetails.EventID != null;

    const [isLoading, setIsLoading] = useState(false);
    const [errorForm, setErrorForm] = useState(false); // To trigger when the user tries to go next/submit but the form is incorrect

    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [confirmationConfig, setConfirmationConfig] = useState({});

    // Event information
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [subcategory, setSubcategory] = useState("");
    const [description, setDescription] = useState("");
    const [owner, setOwner] = useState(null);
    // Event Duration
    const [eventMultiDay, setEventMultiDay] = useState(false);
    const [start, setStart] = useState(dayjs());
    const [end, setEnd] = useState(dayjs());
    // Event recurrence
    const [makeRecurring, setMakeRecurring] = useState(false);
    const [errorRecurrence, setErrorRecurrence] = useState(false);
    const [rrule, setRrule] = useState("");
    const [repeatStart, setRepeatStart] = useState(null);
    const [repeatEnd, setRepeatEnd] = useState(null);
    // Event Impact
    const [impactStart, setImpactStart] = useState(null);
    const [impactEnd, setImpactEnd] = useState(null);
    const [expHeadcount, setExpHeadcount] = useState(null);
    const [expRoomNights, setExpRoomNights] = useState(null);
    const [expAvgRate, setExpAvgRate] = useState(null);
    const [expRevenue, setExpRevenue] = useState(null);
    const [plannedBudget, setPlannedBudget] = useState(null);
    const [actualSpend, setActualSpend] = useState(null);

    const normalizeEventDetails = useMemo(() => {
        const details = eventDetails ?? {};

        return {
            Name: details.Name ?? "",
            Category: details.Category ?? "",
            Subcategory: details.Subcategory ?? "",
            Description: details.Description ?? "",
            FromDate: details.FromDate
                ? dayjs(details.FromDate).format("YYYY-MM-DD")
                : dayjs().format("YYYY-MM-DD"), // default = today
            ToDate: details.ToDate
                ? dayjs(details.ToDate).format("YYYY-MM-DD")
                : dayjs().format("YYYY-MM-DD"), // default = today
            RepeatRule: details.RepeatRule ?? "",
            RepeatStart: details.RepeatStart
                ? dayjs(details.RepeatStart).format("YYYY-MM-DD")
                : null,
            RepeatEnd: details.RepeatEnd
                ? dayjs(details.RepeatEnd).format("YYYY-MM-DD")
                : null,
            Owner:
                details.Owner !== undefined && details.Owner !== null
                    ? String(details.Owner)
                    : null,
            ImpactStart: details.ImpactStart
                ? dayjs(details.ImpactStart).format("YYYY-MM-DD")
                : null,
            ImpactEnd: details.ImpactEnd
                ? dayjs(details.ImpactEnd).format("YYYY-MM-DD")
                : null,
            ExpHeadcount: details.ExpHeadcount ?? null,
            ExpRoomNights: details.ExpRoomNights ?? null,
            ExpAvgRate: details.ExpAvgRate ?? null,
            ExpRevenue: details.ExpRevenue ?? null,
            PlannedBudget: details.PlannedBudget ?? null,
            ActualSpend: details.ActualSpend ?? null,
        };
    }, [eventDetails]);

    useEffect(() => {
        if (!open) return;
        // console.log("Event data normalizing", { normalizeEventDetails });

        setName(normalizeEventDetails.Name);
        setCategory(normalizeEventDetails.Category);
        setSubcategory(normalizeEventDetails.Subcategory);
        setDescription(normalizeEventDetails.Description);
        setStart(dayjs(normalizeEventDetails.FromDate));
        setEnd(dayjs(normalizeEventDetails.ToDate));
        setMakeRecurring(!!normalizeEventDetails.RepeatRule);
        setRrule(normalizeEventDetails.RepeatRule);
        setRepeatStart(
            normalizeEventDetails.RepeatStart
                ? dayjs(normalizeEventDetails.RepeatStart)
                : normalizeEventDetails.RepeatStart,
        );
        setRepeatEnd(
            normalizeEventDetails.RepeatEnd
                ? dayjs(normalizeEventDetails.RepeatEnd)
                : normalizeEventDetails.RepeatEnd,
        );
        setOwner(normalizeEventDetails.Owner);

        setImpactStart(
            normalizeEventDetails.ImpactStart
                ? dayjs(normalizeEventDetails.ImpactStart)
                : normalizeEventDetails.ImpactStart,
        );
        setImpactEnd(
            normalizeEventDetails.ImpactEnd
                ? dayjs(normalizeEventDetails.ImpactEnd)
                : normalizeEventDetails.ImpactEnd,
        );
        setExpHeadcount(normalizeEventDetails.ExpHeadcount);
        setExpRoomNights(normalizeEventDetails.ExpRoomNights);
        setExpAvgRate(normalizeEventDetails.ExpAvgRate);
        setExpRevenue(normalizeEventDetails.ExpRevenue);
        setPlannedBudget(normalizeEventDetails.PlannedBudget);
        setActualSpend(normalizeEventDetails.ActualSpend);

        if (normalizeEventDetails.FromDate !== normalizeEventDetails.ToDate)
            setEventMultiDay(true);
    }, [normalizeEventDetails, open, editEvent]);

    const arraysEqualIgnoringOrder = (a = [], b = []) => {
        if (a.length !== b.length) return false;
        const sa = [...a].sort();
        const sb = [...b].sort();
        return sa.every((v, i) => v === sb[i]);
    };

    const getModifiedFields = () => {
        const original = normalizeEventDetails;
        const changed = {};
        const fieldsToCompare = {
            Name: name,
            Category: category,
            Subcategory: subcategory,
            Description: description,
            FromDate: start.format("YYYY-MM-DD"),
            ToDate: end.format("YYYY-MM-DD"),
            RepeatRule: makeRecurring ? rrule : "",
            RepeatStart: makeRecurring
                ? repeatStart?.format("YYYY-MM-DD")
                : null,
            RepeatEnd: makeRecurring ? repeatEnd?.format("YYYY-MM-DD") : null,
            Owner: owner ?? null,
            ImpactStart: impactStart
                ? dayjs(impactStart).format("YYYY-MM-DD")
                : null,
            ImpactEnd: impactEnd ? dayjs(impactEnd).format("YYYY-MM-DD") : null,
            ExpHeadcount: expHeadcount ?? null,
            ExpRoomNights: expRoomNights ?? null,
            ExpAvgRate: expAvgRate ?? null,
            ExpRevenue: expRevenue ?? null,
            PlannedBudget: plannedBudget ?? null,
            ActualSpend: actualSpend ?? null,
        };
        for (const [key, value] of Object.entries(fieldsToCompare)) {
            const originalValue =
                original[key] ?? (Array.isArray(value) ? [] : null);
            if (Array.isArray(value)) {
                if (!arraysEqualIgnoringOrder(value, originalValue))
                    changed[key] = value;
            } else if (value !== originalValue) {
                changed[key] = value;
            }
        }

        return changed;
    };

    const cleanClose = () => {
        setName("");
        setCategory("");
        setSubcategory("");
        setDescription("");
        setOwner(null);

        setStart(dayjs());
        setEnd(dayjs());

        setMakeRecurring(false);
        setErrorRecurrence(false);
        setRrule("");

        setErrorForm(false);
        // External handling
        onClose();
    };

    const handleClose = () => {
        const wasModified = Object.keys(getModifiedFields()).length;
        if (wasModified) {
            const confirmConfig = {
                title: "Discard changes?",
                description: `Are you sure you want to discard?`,
                answers: [
                    { title: "Cancel", value: "cancel", onSelect: () => {} },
                    {
                        title: "Discard",
                        value: "discard",
                        onSelect: () => {
                            cleanClose();
                        },
                    },
                ],
            };
            setConfirmationConfig(confirmConfig);

            setOpenConfirmationModal(true);
        } else {
            cleanClose();
        }
    };

    /**
     * Keep date locks and integrity between each other:
     * end >= start ; start = repStart ; repEnd > repStart ;
     * repEnd < end can happen, i.e. an event that last 6 weeks, and repeats every month.
     * Even if this is rare, it's not breaking any rule.
     * @param {*} date
     * @param {*} changed
     */
    const handleDateIntegrity = (date, changed = "start") => {
        if (changed === "start") {
            setStart(date);
            if (end < date || !eventMultiDay) setEnd(date);
            if (makeRecurring) setRepeatStart(date);
        } else if (changed === "end") {
            setEnd(date);
            if (date < start) {
                setStart(date);
                if (makeRecurring) setRepeatStart(date);
            }
        }
    };

    const validateForm = () => {
        let isValid = true;
        setErrorForm(false);

        if (
            name.trim().length < 3 ||
            !Object.keys(CATEGORY).includes(category) ||
            !start ||
            !end ||
            dayjs(start) > dayjs(end) ||
            (makeRecurring && errorRecurrence) ||
            (category === "ppc" && !plannedBudget)
        ) {
            setErrorForm(true);
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async () => {
        // New event
        if (!editEvent || copiedEvent) {
            const payloadPost = buildEventPayload(false, null);
            submit(payloadPost);
        } else {
            // Patch
            const modifiedFields = getModifiedFields();
            const hasChanges = Object.keys(modifiedFields).length > 0;
            const payloadSingle = buildEventPayload(
                true,
                "single",
                modifiedFields,
            );
            const payloadFuture = buildEventPayload(
                true,
                "future",
                modifiedFields,
            );
            const payloadSeries = buildEventPayload(
                true,
                "series",
                modifiedFields,
            );
            const payloadOnly = buildEventPayload(true, "only", modifiedFields);

            // Non series
            if (hasChanges && !eventDetails.RepeatRule) {
                await submit(payloadSingle);
            } else if (hasChanges && eventDetails.RepeatRule) {
                // Series
                const confirmConfig = { title: "Editing a series" };
                const isMakingNonRecurring =
                    eventDetails.RepeatRule && !makeRecurring;
                const reccurrenceChanged =
                    modifiedFields.hasOwnProperty("RepeatRule");
                const isOriginalEvent =
                    eventDetails.EventID === eventDetails.RecurrenceID ||
                    !eventDetails.RecurrenceID;

                // 3 actions depending on what was changed:
                if (isMakingNonRecurring) {
                    // Series transform to unique event (single)
                    confirmConfig.description =
                        "Removing recurrency will convert the series into a unique event.";
                    confirmConfig.answers = [
                        {
                            title: "Cancel",
                            value: "cancel",
                            onSelect: () => {},
                        },
                        {
                            title: "Accept",
                            value: "accept",
                            onSelect: () => {
                                setOpenConfirmationModal(false);
                                submit(payloadSingle);
                            },
                        },
                    ];
                } else if (reccurrenceChanged) {
                    // Series with reccurrence changed (future, series)
                    confirmConfig.description =
                        "Recurrence has been changed, how do you want to apply the changes?";
                    confirmConfig.answers = [
                        {
                            title: "This and future",
                            value: "future",
                            onSelect: async () => {
                                setOpenConfirmationModal(false);
                                if (isOriginalEvent) {
                                    await submit(payloadSeries); // If its the original, it's the same as updating the series
                                } else {
                                    await submit(payloadFuture);
                                }
                            },
                            severity: 2,
                        },
                        {
                            title: "Entire series",
                            value: "series",
                            onSelect: async () => {
                                setOpenConfirmationModal(false);

                                await submit(payloadSeries);
                            },
                            severity: 3,
                        },
                    ];
                } else {
                    // Series with general changes (only, series, future)
                    confirmConfig.description =
                        "How do you want to apply the edit?";
                    confirmConfig.answers = [
                        {
                            title: "Only this",
                            value: "only",
                            onSelect: async () => {
                                setOpenConfirmationModal(false);
                                await submit(payloadOnly);
                            },
                            severity: 1,
                        },
                        {
                            title: "This and future",
                            value: "future",
                            onSelect: async () => {
                                if (isOriginalEvent) {
                                    await submit(payloadSeries);
                                } else {
                                    await submit(payloadFuture);
                                }
                            },
                            severity: 2,
                        },
                        {
                            title: "Entire series",
                            value: "series",
                            onSelect: async () => {
                                setOpenConfirmationModal(false);
                                await submit(payloadSeries);
                            },
                            severity: 3,
                        },
                    ];
                }

                setConfirmationConfig(confirmConfig);
                setOpenConfirmationModal(true);
            } else {
                // else = no changes have been made
                cleanClose();
            }
        }
    };

    const buildEventPayload = (isPatch, mode = null, modifiedFields = {}) => {
        // console.log("Payload build", { start, end, impactStart, impactEnd });
        const payload = {
            Name: name,
            FromDate: start?.format("YYYY-MM-DD"),
            ToDate: end?.format("YYYY-MM-DD"),
            Category: category,
            Subcategory: subcategory,
            Description: description,
            HotelID: SanitizeHotelID(hotelID, GBH_ID),
            Owner: owner,
            RepeatRule: makeRecurring ? rrule : null,
            RepeatStart: makeRecurring
                ? repeatStart?.format("YYYY-MM-DD")
                : null,
            RepeatEnd: makeRecurring ? repeatEnd?.format("YYYY-MM-DD") : null,
            RecurrenceID:
                isPatch && eventDetails.RepeatRule !== ""
                    ? (eventDetails["RecurrenceID"] ?? eventDetails.EventID)
                    : null,
            // Opt fields
            ImpactStart: impactStart ? impactStart.format("YYYY-MM-DD") : null,
            ImpactEnd: impactEnd ? impactEnd.format("YYYY-MM-DD") : null,
            ExpHeadcount: expHeadcount ?? null,
            ExpRoomNights: expRoomNights ?? null,
            ExpAvgRate: expAvgRate ?? null,
            ExpRevenue: expRevenue ?? null,
            PlannedBudget: plannedBudget ?? null,
            ActualSpend: actualSpend ?? null,
        };

        if (isPatch) {
            payload.mode = mode;
            payload.UpdatedBy = user.user_id;
            if (
                makeRecurring &&
                mode === "future" &&
                !modifiedFields.hasOwnProperty("RepeatStart")
            ) {
                // 'Future' implies a cut from the event that is being edited (RepeatStart needs to be reset)
                payload.RepeatStart = normalizeEventDetails.FromDate;
                payload.RepeatRule = updateStartEndRRule(
                    payload.RepeatStart,
                    null,
                    payload.RepeatRule,
                );
            } else if (makeRecurring && mode === "only") {
                payload.exceptionDate = normalizeEventDetails.FromDate;
            } else if (makeRecurring && mode === "series") {
                if (modifiedFields.hasOwnProperty("FromDate"))
                    payload.FromDate = start?.format("YYYY-MM-DD");
                else payload.FromDate = null;
                if (modifiedFields.hasOwnProperty("ToDate")) {
                    const newDuration = end.diff(start, "day");
                    const parentFromDate = normalizeEventDetails.RepeatStart;
                    payload.ToDate = dayjs(parentFromDate)
                        .add(newDuration, "day")
                        .format("YYYY-MM-DD");
                } else {
                    payload.ToDate = null;
                }
            }
        } else {
            payload.HotelID = SanitizeHotelID(hotelID, GBH_ID);
            payload.UserID = user.user_id;
        }

        return Object.fromEntries(
            Object.entries(payload).filter(([_, v]) => v !== undefined),
        );
    };

    const submit = async (payload) => {
        try {
            const isPatch = editEvent && !copiedEvent;

            let eventID = eventDetails.EventID;
            if (eventDetails.RepeatRule) {
                eventID = eventDetails.RecurrenceID ?? eventDetails.EventID;
            }

            const response = isPatch
                ? await requestUpdateEvent(eventID, payload, headers)
                : await requestCreateEvent(payload, headers);

            if (response.status === 200) {
                handleUpdate({
                    update: true,
                    alert: {
                        message: isPatch ? "Changes saved" : "Event created",
                        severity: "success",
                    },
                });
                cleanClose();
            }
        } catch (error) {
            console.error("Error submitting the event:", error);
            handleUpdate({
                update: false,
                alert: {
                    message: `Submit failed - ${error}`,
                    severity: "error",
                },
            });
        }
    };

    const blockNonIntegerKeys = (e) => {
        if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
    };

    return (
        <>
            <Container maxWidth={false}>
                <Stack direction="row" sx={{ mt: 4, mb: 2 }} spacing={2}>
                    <IconButton onClick={() => handleClose()}>
                        <Icons.ArrowBack fontSize="small" />
                    </IconButton>
                    <Typography variant="h4">
                        {editEvent && !copiedEvent
                            ? "Edit Event"
                            : "Add New Event"}
                    </Typography>
                </Stack>
                <form>
                    {/** Event Information */}
                    <Paper sx={{ p: 4, borderRadius: 4 }}>
                        <Stack direction="column" spacing={2}>
                            <Typography
                                variant="h5"
                                fontWeight={"bold"}
                                id="eventform-event-information-title"
                            >
                                Event Information
                            </Typography>

                            <Stack spacing={2}>
                                {/** EVENT NAME */}

                                <ValidatedTextField
                                    id="eventform-name"
                                    label="Event name"
                                    required
                                    value={name}
                                    onChange={(v) => setName(v)}
                                    maxLength={100}
                                    validate={(v) => v.trim().length >= 3}
                                    errorMessage={
                                        "Must be at least 3 characters"
                                    }
                                    errorForm={errorForm}
                                />

                                {/** CATEGORY - SUBCATEGORY */}
                                <Stack spacing={1} direction="row">
                                    <DropdownSearchSelector
                                        label="Category"
                                        options={Object.keys(CATEGORY).map(
                                            (key) => [
                                                key,
                                                CATEGORY[key].label ?? "",
                                                CATEGORY[key].order ?? 0,
                                            ],
                                        )}
                                        selected={category}
                                        setSelected={setCategory}
                                        error={
                                            errorForm &&
                                            !Object.keys(CATEGORY).includes(
                                                category,
                                            )
                                        }
                                        required
                                    />
                                    {/** SUBCATEGORY */}

                                    <ValidatedTextField
                                        id="eventform-subcategory"
                                        label="Subcategory"
                                        value={subcategory}
                                        onChange={(v) => setSubcategory(v)}
                                        maxLength={100}
                                        errorMessage={
                                            "Must be at least 3 characters"
                                        }
                                        multiline
                                        maxRows={3}
                                    />
                                </Stack>
                                {/** DESCRIPTION */}
                                <ValidatedTextField
                                    id="eventform-description"
                                    label="Description"
                                    value={description}
                                    onChange={(v) => setDescription(v)}
                                    maxLength={500}
                                    errorMessage={
                                        "Must be at least 10 characters"
                                    }
                                    multiline
                                    maxRows={3}
                                    minRows={3}
                                />

                                <Stack spacing={1}>
                                    {/* Event Owner */}
                                    <DropdownSearchSelector
                                        label="Event owner"
                                        options={Object.keys(OWNER).map(
                                            (key) => [key, OWNER[key].label],
                                        )}
                                        selected={owner}
                                        setSelected={setOwner}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </Paper>
                    <br />

                    {/** Event Duration */}
                    <Paper sx={{ p: 4, borderRadius: 4 }}>
                        <Stack direction="column" spacing={2}>
                            <Typography
                                variant="h5"
                                fontWeight={"bold"}
                                id="Event-form-event-information-title"
                            >
                                Event Duration
                            </Typography>

                            <Container
                                maxWidth={false}
                                sx={{
                                    p: 2,
                                    backgroundColor: Colours.lightBubble,
                                    borderRadius: 4,
                                    border: !eventMultiDay
                                        ? `2px solid ${Colours.attract}`
                                        : "none",
                                }}
                            >
                                <Stack spacing={2}>
                                    <FormControlLabel
                                        value="OneDayEvent"
                                        control={
                                            <Radio
                                                checked={!eventMultiDay}
                                                onClick={() => {
                                                    if (eventMultiDay) {
                                                        handleDateIntegrity(
                                                            start,
                                                            "end",
                                                        );
                                                        // if (makeRecurring)
                                                        //     setRepeatStart(
                                                        //         start,
                                                        //     );
                                                    }

                                                    setEventMultiDay(false);
                                                }}
                                                sx={{ pl: 0 }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                sx={{
                                                    flexGrow: 1,
                                                }}
                                                variant="subtitle1"
                                                fontWeight={"bold"}
                                            >
                                                One-day Event
                                            </Typography>
                                        }
                                    />

                                    {!eventMultiDay && (
                                        <CustomDateButton
                                            date={start}
                                            label="Event Date"
                                            onChange={(val) =>
                                                handleDateIntegrity(
                                                    val,
                                                    "start",
                                                )
                                            }
                                            error={start > end}
                                        />
                                    )}
                                </Stack>
                            </Container>
                            <Container
                                maxWidth={false}
                                sx={{
                                    p: 2,
                                    backgroundColor: Colours.lightBubble,
                                    borderRadius: 4,
                                    border: eventMultiDay
                                        ? `2px solid ${Colours.attract}`
                                        : "none",
                                }}
                            >
                                <Stack spacing={2}>
                                    <FormControlLabel
                                        value="MultiDayEvent"
                                        control={
                                            <Radio
                                                checked={eventMultiDay}
                                                onClick={() => {
                                                    setEventMultiDay(true);
                                                    // if (makeRecurring)
                                                    //     setRepeatStart(start);
                                                }}
                                                sx={{ pl: 0 }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                sx={{
                                                    flexGrow: 1,
                                                }}
                                                variant="subtitle1"
                                                fontWeight={"bold"}
                                            >
                                                Multi-day Event
                                            </Typography>
                                        }
                                    />
                                    {eventMultiDay && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            // sx={{
                                            //     justifyContent: "space-between",
                                            // }}
                                        >
                                            <CustomDateButton
                                                date={start}
                                                label="Start Date"
                                                onChange={(val) =>
                                                    handleDateIntegrity(
                                                        val,
                                                        "start",
                                                    )
                                                }
                                                error={start > end}
                                            />

                                            <CustomDateButton
                                                date={end}
                                                label="End Date"
                                                onChange={(val) =>
                                                    handleDateIntegrity(
                                                        val,
                                                        "end",
                                                    )
                                                }
                                                error={start > end}
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                            </Container>
                        </Stack>
                    </Paper>
                    <br />
                    {/** Event Recurrence */}
                    <Paper sx={{ p: 4, borderRadius: 4 }}>
                        <Stack direction="column" spacing={2}>
                            <Typography
                                variant="h5"
                                fontWeight={"bold"}
                                id="eventform-event-recurrence-title"
                            >
                                Recurrence
                            </Typography>
                            <Typography
                                fontWeight={"bold"}
                                id="eventform-repeat-this-event-label"
                                variant="subtitle1"
                            >
                                Repeat this event?
                            </Typography>
                            <Stack direction="row" spacing={5}>
                                <FormControlLabel
                                    value="No-recurrence"
                                    control={
                                        <Radio
                                            checked={!makeRecurring}
                                            onClick={() => {
                                                setMakeRecurring(false);
                                                setRepeatStart(null);
                                            }}
                                            sx={{ pl: 0 }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                flexGrow: 1,
                                            }}
                                            variant="subtitle1"
                                            fontWeight={"bold"}
                                        >
                                            No
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value="Yes-recurrence"
                                    control={
                                        <Radio
                                            checked={makeRecurring}
                                            onClick={() => {
                                                setMakeRecurring(true);
                                                if (repeatStart === null)
                                                    setRepeatStart(start);
                                            }}
                                            sx={{ pl: 0 }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                flexGrow: 1,
                                            }}
                                            variant="subtitle1"
                                            fontWeight={"bold"}
                                        >
                                            Yes
                                        </Typography>
                                    }
                                />
                            </Stack>
                            {makeRecurring && (
                                <>
                                    <Divider />
                                    <EventRepeatForm
                                        onChange={(rr, repEnd = null) => {
                                            setRrule(rr);

                                            if (repEnd) {
                                                setRepeatEnd(repEnd);
                                            }
                                        }}
                                        rrule={rrule}
                                        start={repeatStart}
                                        setErrorRecurrence={setErrorRecurrence}
                                    />
                                    <Container
                                        maxWidth={false}
                                        component={"Paper"}
                                        sx={{
                                            p: 2,
                                            backgroundColor:
                                                Colours.lightBubble,
                                            borderRadius: 4,
                                            borderLeft: `5px solid ${Colours.attract} `,
                                        }}
                                    >
                                        <Typography
                                            variant={"subtitle1"}
                                            fontWeight={"bold"}
                                            sx={{ whiteSpace: "pre-line" }}
                                        >
                                            {describeRRule(rrule, repeatStart)}
                                        </Typography>
                                    </Container>
                                    {!errorRecurrence && (
                                        <EventOccurrencesViewer
                                            rrule={rrule}
                                            start={start}
                                            end={end}
                                            repeatStart={repeatStart}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    </Paper>
                    <br />
                    {/** Event Impact */}
                    <Paper
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            backgroundColor: "transparent",
                            border: `2px solid ${Colours.notificationCard}`,
                        }}
                    >
                        <Stack direction="column" spacing={2}>
                            <Stack direction="row" spacing={4}>
                                <Typography
                                    variant="h5"
                                    fontWeight={"bold"}
                                    id="eventform-event-impact-title"
                                >
                                    Event Impact
                                </Typography>
                                <Typography
                                    fontSize={"small"}
                                    id="eventform-event-impact-optional-subtitle"
                                    sx={{
                                        p: 1,
                                        px: 1.5,
                                        borderRadius: 20,
                                        backgroundColor:
                                            Colours.notificationCard,
                                    }}
                                >
                                    Optional
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <CustomDateButton
                                    date={impactStart}
                                    required={false}
                                    label="Impact Period Start"
                                    onChange={(val) => {
                                        setImpactStart(val);
                                        if (!impactEnd || val > impactEnd)
                                            setImpactEnd(val);
                                    }}
                                    error={impactStart > impactEnd}
                                />

                                <CustomDateButton
                                    date={impactEnd}
                                    required={false}
                                    label="Impact Period End"
                                    onChange={(val) => {
                                        setImpactEnd(val);
                                        if (!impactStart || val < impactStart)
                                            setImpactStart(val);
                                    }}
                                    error={impactStart > impactEnd}
                                />
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Stack
                                    direction="column"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={"bold"}
                                        sx={{ alignContent: "baseline" }}
                                    >
                                        Expected Headcount
                                    </Typography>
                                    <TextField
                                        id="eventform-event-impact-headcount-textfield"
                                        type="number"
                                        value={expHeadcount ?? ""}
                                        placeholder={"e.g. 2"}
                                        onKeyDown={blockNonIntegerKeys}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setExpHeadcount(null);
                                                return;
                                            }
                                            let num = Math.round(Number(raw));
                                            if (num < 1) num = 1;
                                            setExpHeadcount(num);
                                        }}
                                        inputProps={{ min: 1 }}
                                    />
                                </Stack>
                                <Stack
                                    direction="column"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={"bold"}
                                        sx={{ alignContent: "baseline" }}
                                    >
                                        Expected Room Nights
                                    </Typography>
                                    <TextField
                                        id="eventform-event-impact-roomNights-textfield"
                                        type="number"
                                        value={expRoomNights ?? ""}
                                        placeholder={"e.g. 4"}
                                        onKeyDown={blockNonIntegerKeys}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setExpRoomNights(null);
                                                setExpRevenue(null);
                                                return;
                                            }
                                            let num = Math.round(Number(raw));
                                            if (num < 1) num = 1;
                                            setExpRoomNights(num);

                                            if (num >= 1 && expAvgRate >= 1) {
                                                setExpRevenue(num * expAvgRate);
                                            }
                                        }}
                                        inputProps={{ min: 1 }}
                                    />
                                </Stack>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <Stack
                                    direction="column"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={"bold"}
                                        sx={{ alignContent: "baseline" }}
                                    >
                                        Expected Average Rate
                                    </Typography>
                                    <TextField
                                        id="eventform-event-impact-avgRate-textfield"
                                        type="number"
                                        value={expAvgRate ?? ""}
                                        placeholder={"e.g. 150"}
                                        onKeyDown={blockNonIntegerKeys}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setExpAvgRate(null);
                                                setExpRevenue(null);
                                                return;
                                            }
                                            let num = Math.round(Number(raw));
                                            if (num < 1) num = 1;
                                            setExpAvgRate(num);

                                            if (num >= 1 && expRoomNights >= 1)
                                                setExpRevenue(
                                                    num * expRoomNights,
                                                );
                                        }}
                                        inputProps={{ min: 1 }}
                                    />
                                </Stack>
                                <Stack
                                    direction="column"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={"bold"}
                                        sx={{ alignContent: "baseline" }}
                                    >
                                        Expected Revenue ({currencySymbol})
                                    </Typography>
                                    <TextField
                                        id="eventform-event-impact-revenue-textfield"
                                        type="number"
                                        value={expRevenue ?? ""}
                                        disabled={true}
                                        placeholder={"—"}
                                        helperText={
                                            "Auto-calculated from Room Nights x Average Rate"
                                        }
                                    />
                                </Stack>
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <Stack
                                    direction="column"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={"bold"}
                                        sx={{ alignContent: "baseline" }}
                                    >
                                        Planned Budget ({currencySymbol})
                                        {category === "ppc" && (
                                            <span style={{ color: "red" }}>
                                                {" *"}
                                            </span>
                                        )}
                                    </Typography>
                                    <TextField
                                        id="eventform-event-impact-plannedBudget-textfield"
                                        type="number"
                                        value={plannedBudget ?? ""}
                                        placeholder={"e.g. 5000"}
                                        onKeyDown={blockNonIntegerKeys}
                                        error={
                                            errorForm &&
                                            category === "ppc" &&
                                            !plannedBudget
                                        }
                                        helperText={
                                            errorForm &&
                                            category === "ppc" &&
                                            !plannedBudget
                                                ? "Required for PPC events"
                                                : ""
                                        }
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setPlannedBudget(null);
                                                return;
                                            }
                                            let num = Math.round(Number(raw));
                                            if (num < 1) num = 1;
                                            setPlannedBudget(num);
                                        }}
                                        inputProps={{ min: 1 }}
                                    />
                                </Stack>
                                <Stack
                                    direction="column"
                                    sx={{ width: "100%" }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={"bold"}
                                        sx={{ alignContent: "baseline" }}
                                    >
                                        Actual Spend ({currencySymbol})
                                    </Typography>
                                    <TextField
                                        id="eventform-event-impact-actualSpend-textfield"
                                        type="number"
                                        value={actualSpend ?? ""}
                                        placeholder={"e.g. 4200"}
                                        onKeyDown={blockNonIntegerKeys}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setActualSpend(null);
                                                return;
                                            }
                                            let num = Math.round(Number(raw));
                                            if (num < 1) num = 1;
                                            setActualSpend(num);
                                        }}
                                        inputProps={{ min: 1, step: 1 }}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </Paper>
                </form>
                <br />
                <Stack justifyContent={"right"} direction={"row"} spacing={2}>
                    <Button
                        disabled={isLoading}
                        variant="outlined"
                        onClick={() => handleClose()}
                        sx={{
                            fontSize: "large",
                            color: "black",
                            textTransform: "none",
                        }}
                    >
                        <>
                            <Icons.CloseNavbar sx={{ paddingRight: 1 }} />
                            Cancel
                        </>
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={isLoading}
                        onClick={async () => {
                            setIsLoading(true);
                            let isValidForm = validateForm();
                            if (isValidForm) {
                                await handleSubmit();
                            } else {
                                handleUpdate({
                                    update: false,
                                    alert: {
                                        message: `Fix all errors before saving`,
                                        severity: "error",
                                    },
                                });
                            }
                            setIsLoading(false);
                        }}
                        sx={{
                            paddingY: 1,
                            paddingX: 4,
                            fontSize: "large",
                            color: "black",
                            textTransform: "none",
                            backgroundColor: Colours.primary,
                            "&:hover": {
                                backgroundColor: Colours.secondarySoft,
                            },
                        }}
                    >
                        <>
                            <Icons.SaveOutlined sx={{ paddingRight: 1 }} />
                            {editEvent && !copiedEvent
                                ? "Edit Event"
                                : "Create Event"}
                        </>
                    </Button>
                </Stack>
            </Container>

            <ModalConfirmation
                open={openConfirmationModal}
                setOpen={setOpenConfirmationModal}
                title={confirmationConfig.title ?? ""}
                description={confirmationConfig.description ?? ""}
                answers={confirmationConfig.answers ?? []}
                setAnswer={() => {}}
            />
        </>
    );
};

export default EventCreateForm;
